import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { usePortal, PORTAL_RENTALS, PORTAL_TRUCKING, PORTAL_LABELS } from "@/context/PortalContext";
import api from "@/lib/api";
import { LOGO_URL, CONTACT, ROLE_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Menu, ChevronDown, LogOut, LayoutDashboard, User, Phone, Mail, MapPin,
  Bell, Car, Truck, Repeat,
} from "lucide-react";

function NavLink({ to, children, testid }) {
  const loc = useLocation();
  const active = loc.pathname === to || (to !== "/" && loc.pathname.startsWith(to));
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`text-sm font-semibold tracking-tight transition-colors px-3 py-2 rounded-md ${
        active ? "text-[#E32636]" : "text-slate-700 hover:text-slate-900"
      }`}
    >
      {children}
    </Link>
  );
}

function NotificationBell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const seenIds = useRef(new Set());
  const firstLoad = useRef(true);

  const fireOSNotification = (n) => {
    if (typeof window === "undefined") return;
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    if (document.hasFocus() && !document.hidden) return; // skip if user is already looking
    try {
      const notif = new Notification(n.title || "Cleavie's Enterprise", {
        body: n.message || "",
        icon: LOGO_URL,
        tag: n.id,
      });
      notif.onclick = () => {
        window.focus();
        if (n.link) nav(n.link);
        notif.close();
      };
    } catch {}
  };

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
      // Fire OS notifications for any IDs we haven't seen yet (except very first load)
      if (!firstLoad.current) {
        for (const n of data) {
          if (!seenIds.current.has(n.id) && !n.read) {
            fireOSNotification(n);
          }
        }
      }
      data.forEach((n) => seenIds.current.add(n.id));
      firstLoad.current = false;
    } catch {}
  };

  useEffect(() => {
    if (!user) return;
    // Ask for permission once
    if (typeof window !== "undefined" && "Notification" in window
        && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const unread = items.filter(i => !i.read).length;

  if (!user) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notif-bell">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-[#E32636] text-white text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {items.length > 0 && (
            <button
              data-testid="notif-mark-all"
              className="text-xs text-[#1D4ED8] hover:underline"
              onClick={async () => { await api.post("/notifications/read-all"); load(); }}
            >Mark all read</button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-slate-500">No notifications yet.</div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {items.slice(0, 12).map((n) => (
              <button
                key={n.id}
                data-testid={`notif-item-${n.id}`}
                onClick={async () => {
                  await api.post(`/notifications/${n.id}/read`);
                  load();
                  if (n.link) nav(n.link);
                  setOpen(false);
                }}
                className={`w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 ${n.read ? "opacity-60" : ""}`}
              >
                <div className="text-sm font-bold text-slate-900">{n.title}</div>
                <div className="text-xs text-slate-600 line-clamp-2">{n.message}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
              </button>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PortalSwitcher() {
  const { user } = useAuth();
  const { portal, setPortal } = usePortal();
  const nav = useNavigate();
  if (!user) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2 hidden md:flex" data-testid="portal-switcher-btn">
          {portal === PORTAL_TRUCKING ? <Truck className="h-4 w-4 text-[#FFCE00]" /> : <Car className="h-4 w-4 text-[#E32636]" />}
          <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
            {portal ? PORTAL_LABELS[portal] : "Pick portal"}
          </span>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Switch portal</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="ps-rentals" onClick={() => { setPortal(PORTAL_RENTALS); nav("/portal/rentals"); }}>
          <Car className="h-4 w-4 mr-2 text-[#E32636]" /> Vehicle Rentals & Leasing
          {portal === PORTAL_RENTALS && <span className="ml-auto text-[#1D4ED8]">●</span>}
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="ps-trucking" onClick={() => { setPortal(PORTAL_TRUCKING); nav("/portal/trucking"); }}>
          <Truck className="h-4 w-4 mr-2 text-[#FFCE00]" /> Trucking & Industrial Services
          {portal === PORTAL_TRUCKING && <span className="ml-auto text-[#1D4ED8]">●</span>}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="ps-select" onClick={() => nav("/portal-select")}>
          <Repeat className="h-4 w-4 mr-2" /> Portal selector
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Navbar() {
  const { user, logout, switchRole } = useAuth();
  const { setPortal } = usePortal();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/90 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <img src={LOGO_URL} alt="Cleavie's Enterprise" className="h-10 w-10 object-contain" />
            <div className="hidden sm:block leading-tight">
              <div className="font-heading font-black text-base tracking-tight text-slate-900">CLEAVIE'S</div>
              <div className="text-[10px] font-bold tracking-[0.25em] text-[#1D4ED8] -mt-0.5">ENTERPRISE</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            <NavLink to="/" testid="nav-home">Home</NavLink>
            <NavLink to="/portal/rentals" testid="nav-rentals-portal">Rentals & Leasing</NavLink>
            <NavLink to="/portal/trucking" testid="nav-trucking-portal">Trucking & Industrial</NavLink>
            <NavLink to="/about" testid="nav-about">About</NavLink>
            <NavLink to="/contact" testid="nav-contact">Contact</NavLink>
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <>
                <PortalSwitcher />
                <NotificationBell />
                {user.roles && user.roles.length > 1 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="gap-2 hidden sm:flex" data-testid="role-switcher-btn">
                        <span className="font-bold text-xs uppercase tracking-wider text-[#1D4ED8]">
                          {ROLE_LABELS[user.active_role] || "Customer"}
                        </span>
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel>Switch role</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {(user.roles || []).map((r) => (
                        <DropdownMenuItem
                          key={r}
                          data-testid={`switch-role-${r}`}
                          onClick={async () => { await switchRole(r); nav("/dashboard"); }}
                        >
                          {ROLE_LABELS[r]}
                          {r === user.active_role && <span className="ml-auto text-[#1D4ED8]">●</span>}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="gap-2" data-testid="user-menu-btn">
                      <User className="h-4 w-4" />
                      <span className="hidden sm:inline">{user.name?.split(" ")[0] || "Me"}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="font-normal">
                      <div className="text-xs text-slate-500">Signed in as</div>
                      <div className="font-semibold truncate">{user.email}</div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => nav("/dashboard")} data-testid="menu-dashboard">
                      <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => nav("/portal-select")} data-testid="menu-portal">
                      <Repeat className="h-4 w-4 mr-2" /> Choose portal
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={async () => { setPortal(null); await logout(); nav("/"); }} data-testid="menu-logout">
                      <LogOut className="h-4 w-4 mr-2" /> Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/login" className="hidden sm:block">
                  <Button variant="ghost" data-testid="nav-login-btn">Sign in</Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-[#E32636] hover:bg-[#C91F2C] text-white font-bold" data-testid="nav-register-btn">
                    Get started
                  </Button>
                </Link>
              </>
            )}
            <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden pb-3 flex flex-col gap-1">
            <NavLink to="/" testid="mnav-home">Home</NavLink>
            <NavLink to="/portal/rentals" testid="mnav-rentals">Rentals & Leasing</NavLink>
            <NavLink to="/portal/trucking" testid="mnav-trucking">Trucking & Industrial</NavLink>
            <NavLink to="/about" testid="mnav-about">About</NavLink>
            <NavLink to="/contact" testid="mnav-contact">Contact</NavLink>
          </div>
        )}
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-slate-200 bg-white mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Logo" className="h-12 w-12 object-contain" />
            <div>
              <div className="font-heading font-black text-lg text-slate-900">CLEAVIE'S ENTERPRISE</div>
              <div className="text-xs tracking-[0.2em] font-bold text-[#1D4ED8]">ANTIGUA & BARBUDA</div>
            </div>
          </div>
          <p className="text-sm text-slate-600 mt-4 max-w-md">
            One platform · Two portals · Reliable transportation, water, bins, diagnostics, trucking and vehicle rentals across Antigua & Barbuda.
          </p>
        </div>
        <div>
          <div className="tag-overline mb-3">Portals</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/portal/rentals" className="text-slate-700 hover:text-[#E32636]">Vehicle Rentals & Leasing</Link></li>
            <li><Link to="/portal/trucking" className="text-slate-700 hover:text-[#E32636]">Trucking & Industrial</Link></li>
            <li><Link to="/contact" className="text-slate-700 hover:text-[#E32636]">Contact us</Link></li>
          </ul>
        </div>
        <div>
          <div className="tag-overline mb-3">Reach us</div>
          <ul className="space-y-2 text-sm text-slate-700">
            <li className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-[#1D4ED8]" />{CONTACT.address}</li>
            <li className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#1D4ED8]" />{CONTACT.phone}</li>
            <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#1D4ED8]" />{CONTACT.email}</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-200 py-4 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} Cleavie's Enterprise. All rights reserved.
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
